import React from 'react';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import DynamicTable, { type DynamicTableProps } from '@atlaskit/dynamic-table';
import Shortcut from '@atlaskit/icon/glyph/shortcut';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { MST_IMAGE_URL, SLACK_IMAGE_URL } from '../../../../common/constants';
import type { ChatToolsState } from '../../../../services/store/chat-tools/types';
import { Action } from './action';
import { messages } from './messages';

export const CopyIntegrationsTable = ({
	integrations,
}: {
	integrations: ChatToolsState['integrations'];
}) => {
	const { formatMessage } = useIntl();
	if (!integrations.data) {
		throw new Error(
			'CopyIntegrationsTable should be rendered when chat tools integrations are loaded',
		);
	}

	const isOptimalTooling = ff('platform.show-optimal-tooling_brgom');

	const rows: DynamicTableProps['rows'] = integrations.data.values.map((item, index) => {
		const channelName = `${item.msTeamsName || item.channelName}, ${item.chatSpaceName}`;

		return {
			key: `row-${index}`,
			cells: [
				{
					key: `cell-${index}-chat-integration`,
					content: (
						<Tooltip position="top" content={item.name}>
							<Box xcss={isOptimalTooling && nameStyles} paddingInlineStart="space.100">
								<Inline space="space.100" alignBlock="center">
									<Avatar
										size="xsmall"
										src={item.chatSpaceType === 'slack' ? SLACK_IMAGE_URL : MST_IMAGE_URL}
									/>
									<Text as="p" maxLines={1}>
										{item.name}
									</Text>
								</Inline>
							</Box>
						</Tooltip>
					),
				},
				{
					key: `cell-${index}-connected-to`,
					content: (
						<Tooltip position="top" content={channelName}>
							<Text as="p" maxLines={1}>
								{channelName}
							</Text>
						</Tooltip>
					),
				},
				...(isOptimalTooling
					? [
							{
								key: `cell-${index}-connected-to`,
								content: (
									<Tooltip position="top" content={item.teamName}>
										<Text as="p" maxLines={1}>
											{item.teamName}
										</Text>
									</Tooltip>
								),
							},
						]
					: [{}]),
				{
					key: `cell-${index}-status`,
					content: (
						<Inline space="space.050" alignBlock="center" alignInline="center" xcss={inlineStyles}>
							<Action item={item} />
						</Inline>
					),
				},
				...(isOptimalTooling
					? [
							{
								key: `cell-${index}-actions`,
								content: item.status === 'COPIED' && (
									<Button
										appearance="subtle"
										iconBefore={<Shortcut label="" />}
										href={
											item.teamId
												? `/jira/ops/teams/${item.teamId}/integrations/${item.chatSpaceType === 'slack' ? 'slack-plugin' : 'msteams-plugin'}/${item.jsmIntegrationId}`
												: `/jira/settings/products/ops/integrations/${item.chatSpaceType === 'slack' ? 'slack-plugin' : 'msteams-plugin'}/${item.jsmIntegrationId}`
										}
										target="_blank"
									/>
								),
							},
						]
					: [{}]),
			],
		};
	});

	const head: DynamicTableProps['head'] = {
		cells: [
			{
				key: 'chat-integration',
				content: (
					<Box paddingInlineStart="space.100">
						{formatMessage(messages.chatIntegrationNonFinal)}
					</Box>
				),
				isSortable: true,
			},
			{ key: 'connected-to', content: formatMessage(messages.connectedToNonFinal) },
			...(isOptimalTooling
				? [
						{
							key: 'responder-team',
							content: formatMessage(messages.responderTeamNonFinal),
							isSortable: true,
						},
					]
				: [{}]),
			{
				key: 'status',
				content: <Inline alignInline="center">{formatMessage(messages.statusNonFinal)}</Inline>,
			},
			...(isOptimalTooling
				? [
						{
							key: 'actions',
							content: null,
						},
					]
				: [{}]),
		],
	};

	return (
		<DynamicTable
			isLoading={integrations.loading}
			head={head}
			rows={rows}
			rowsPerPage={5}
			defaultPage={1}
		/>
	);
};

const inlineStyles = xcss({
	height: 'size.400',
	cursor: 'default',
});

const nameStyles = xcss({
	maxWidth: '280px',
});
